/**
 * Playio Upsell v1.4.0 — pasek postępu + auto-add prezentu
 * v1.3.7: uses Horizon's native 'cart:update' event to trigger section re-render.
 * v1.3.8: re-render on drawer open + pageshow + handle container replacement by morph.
 * v1.3.9: gift NAME always from translated theme string (| t), product fetch only
 *         for the thumbnail — fixes PL name leaking on translated storefronts (cs).
 * v1.4.0: free-all mode — tier1.threshold=0 znaczy "darmowa dostawa na wszystko":
 *         progres liczy się tylko do prezentu (tier2), znacznik tier1 ukryty,
 *         guardy na dzielenie przez 0 gdy oba progi = 0.
 */
(function () {
  'use strict';

  var giftProductPromise = null;
  var origFetch = window.fetch.bind(window); // save BEFORE patching

  function fetchGiftProduct(handle) {
    if (giftProductPromise) return giftProductPromise;
    if (!handle) return Promise.resolve(null);
    // Locale-aware root (/cs/, /en-eu/, /) so product.title comes back in the
    // buyer's language → gift name in the tier2 copy is translated per market.
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    giftProductPromise = origFetch(root + 'products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return giftProductPromise;
  }

  // --- Gift Manager ---

  class PlayioGiftManager {
    constructor(config) {
      this.config = config;
      this.isProcessing = false;
    }

    getGiftLineItemFlag() {
      return this.config.gift.lineItemFlag || '_playio_gift';
    }

    findGiftInCart(cart) {
      if (!cart || !cart.items) return null;
      var flag = this.getGiftLineItemFlag();
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (item.properties && item.properties[flag] === 'true') return item;
      }
      return null;
    }

    isGiftDismissed(cart) {
      return cart && cart.attributes && cart.attributes._playio_gift_dismissed === 'true';
    }

    async setDismissedFlag(value) {
      try {
        await origFetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: { _playio_gift_dismissed: value ? 'true' : '' }
          })
        });
      } catch (err) {
        console.error('[Playio][Gift] setDismissedFlag error:', err);
      }
    }

    async addGift() {
      var variantId = this.config.shipping.tier2.giftVariantId;
      if (!variantId) {
        console.warn('[Playio][Gift] giftVariantId not configured');
        return false;
      }
      var flag = this.getGiftLineItemFlag();
      var props = {};
      props[flag] = 'true';
      try {
        // Use origFetch — theme refresh handled by 'cart:update' event in syncAndRefresh
        var response = await origFetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: props
          })
        });
        if (!response.ok) {
          var text = await response.text();
          console.warn('[Playio][Gift] addGift failed:', response.status, text);
          return false;
        }
        return true;
      } catch (err) {
        console.error('[Playio][Gift] addGift error:', err);
        return false;
      }
    }

    async removeGift(giftItem) {
      if (!giftItem) return false;
      try {
        // Use origFetch — theme refresh handled by 'cart:update' event in syncAndRefresh
        await origFetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: giftItem.key, quantity: 0 })
        });
        return true;
      } catch (err) {
        console.error('[Playio][Gift] removeGift error:', err);
        return false;
      }
    }

    async syncGift(cart, previousCart) {
      if (this.isProcessing) return false;
      if (!this.config) return false;
      if (!this.config.shipping.tier2.giftAutoAdd) return false;
      if (!this.config.shipping.tier2.giftVariantId) return false;

      this.isProcessing = true;
      try {
        var totalCents = cart.items_subtotal_price || cart.total_price || 0;
        var giftInCart = this.findGiftInCart(cart);
        var giftCents = giftInCart ? giftInCart.final_line_price : 0;
        var effectiveTotal = totalCents - giftCents;
        var threshold = this.config.shipping.tier2.threshold;

        // First page load — reset dismissed flag so returning users get the gift again
        if (!previousCart && this.isGiftDismissed(cart)) {
          await this.setDismissedFlag(false);
        }

        // Detect manual removal by user (only when we have a previous cart to compare)
        if (previousCart) {
          var giftInPrevious = this.findGiftInCart(previousCart);
          var prevTotalCents = previousCart.items_subtotal_price || previousCart.total_price || 0;
          var prevGiftCents = giftInPrevious ? giftInPrevious.final_line_price : 0;
          var prevEffectiveTotal = prevTotalCents - prevGiftCents;

          if (giftInPrevious && !giftInCart && effectiveTotal >= threshold && prevEffectiveTotal >= threshold) {
            await this.setDismissedFlag(true);
            this.isProcessing = false;
            return false;
          }
        }

        // Below threshold: remove gift + reset dismissed
        if (effectiveTotal < threshold && giftInCart) {
          await this.removeGift(giftInCart);
          await this.setDismissedFlag(false);
          this.isProcessing = false;
          return true;
        }

        // Above threshold + no gift + not dismissed: add
        if (effectiveTotal >= threshold && !giftInCart && !this.isGiftDismissed(cart)) {
          var added = await this.addGift();
          this.isProcessing = false;
          return added;
        }

        this.isProcessing = false;
        return false;
      } catch (err) {
        console.error('[Playio][Gift] syncGift error:', err);
        this.isProcessing = false;
        return false;
      }
    }
  }

  // --- Progress Bar ---

  class PlayioShippingProgress {
    constructor(container) {
      this.container = container;
      this.config = window.PlayioConfig;
      if (!this.config) {
        console.error('[Playio] PlayioConfig not loaded');
        return;
      }
      this.giftProduct = null;
      this.init();
    }

    async init() {
      var giftHandle = this.config.shipping.tier2.giftProductHandle;
      if (giftHandle) {
        fetchGiftProduct(giftHandle).then(function (product) {
          this.giftProduct = product;
        }.bind(this));
      }
    }

    isPromoActive() {
      if (!this.config.promo.enabled) return false;
      try {
        return new Date() < new Date(this.config.promo.endsAt);
      } catch (e) {
        return false;
      }
    }

    getGiftInCart(cart) {
      if (!cart || !cart.items) return null;
      var flag = this.config.gift.lineItemFlag || '_playio_gift';
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (item.properties && item.properties[flag] === 'true') return item;
      }
      return null;
    }

    computeState(totalCents, cart) {
      var tier1 = this.config.shipping.tier1;
      var tier2 = this.config.shipping.tier2;
      var promoActive = this.isPromoActive();
      var tier2Active = tier2.activeDuringPromo || !promoActive;

      var giftInCart = this.getGiftInCart(cart);
      var giftCents = giftInCart ? giftInCart.final_line_price : 0;
      var effectiveTotal = totalCents - giftCents;

      var maxThreshold = tier2Active ? tier2.threshold : tier1.threshold;
      var progress = maxThreshold > 0 ? Math.min((effectiveTotal / maxThreshold) * 100, 100) : 100;
      var tier1MarkerPct = tier2Active && tier2.threshold > 0 ? (tier1.threshold / tier2.threshold) * 100 : 100;

      var state, remaining;
      if (effectiveTotal < tier1.threshold) {
        state = 'below_tier1';
        remaining = tier1.threshold - effectiveTotal;
      } else if (tier2Active && effectiveTotal < tier2.threshold) {
        state = 'at_tier1_below_tier2';
        remaining = tier2.threshold - effectiveTotal;
      } else if (tier2Active) {
        state = 'at_tier2';
        remaining = 0;
      } else {
        state = 'at_tier1_promo';
        remaining = 0;
      }

      return { state: state, progress: progress, remaining: remaining, tier1MarkerPct: tier1MarkerPct, tier2Active: tier2Active };
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(this.config.locale, {
        style: 'currency',
        currency: this.config.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(cents / 100);
    }

    getGiftData() {
      // Name ALWAYS from the theme's translated string (config.gift.fallbackName,
      // set via | t in playio-progress-bar.liquid) — localized per storefront and
      // free of the product-title "GRATIS/ZDARMA" prefix. The fetched product is
      // used only for the thumbnail; on translated slugs the PL-handle fetch 404s,
      // which previously fell back to the PL name — that's what we're fixing here.
      var image = this.config.gift.fallbackImage;
      if (this.giftProduct) {
        image = this.giftProduct.featured_image || (this.giftProduct.images && this.giftProduct.images[0]) || image;
      }
      return {
        name: this.config.gift.fallbackName,
        image: image
      };
    }

    renderLine(template, replacements) {
      if (!template) return '';
      var result = template;
      Object.entries(replacements).forEach(function (entry) {
        result = result.replace(new RegExp('{' + entry[0] + '}', 'g'), entry[1]);
      });
      return result;
    }

    render(cart) {
      if (!this.config) return;
      // Handle container replacement by morphSection
      if (!this.container.isConnected) {
        var sel = (this.config.selectors && this.config.selectors.progressContainer) || '[data-playio-progress]';
        var newEl = document.querySelector(sel);
        if (!newEl) return;
        this.container = newEl;
        this.container.__playioInit = true;
      }
      var totalCents = cart.items_subtotal_price || cart.total_price || 0;
      var s = this.computeState(totalCents, cart);
      var copy = this.config.copy;
      var gift = this.getGiftData();

      var replacements = {
        amount: this.formatMoney(s.remaining),
        giftName: gift.name
      };

      var line1Template = copy[s.state + '_line1'] || '';
      var line2Template = copy[s.state + '_line2'] || '';
      var line1 = this.renderLine(line1Template, replacements);
      var line2 = this.renderLine(line2Template, replacements);

      var showGiftThumb = (s.state === 'at_tier1_below_tier2' || s.state === 'at_tier2') && gift.image;
      var iconHtml = '';
      if (s.state === 'below_tier1') {
        iconHtml = '<span class="playio-progress__ship-icon" aria-hidden="true">🚚</span>';
      } else if (showGiftThumb) {
        iconHtml = '<img src="' + gift.image + '" alt="' + this.escapeHtml(gift.name) + '" class="playio-progress__gift-thumb" loading="lazy">';
      }

      this.container.setAttribute('data-playio-state', s.state);
      this.container.innerHTML =
        '<div class="playio-progress" data-state="' + s.state + '">' +
          '<div class="playio-progress__content">' +
            iconHtml +
            '<div class="playio-progress__messages">' +
              '<div class="playio-progress__line playio-progress__line--1">' + line1 + '</div>' +
              (line2 ? '<div class="playio-progress__line playio-progress__line--2">' + line2 + '</div>' : '') +
            '</div>' +
          '</div>' +
          '<div class="playio-progress__bar"' +
               ' role="progressbar"' +
               ' aria-valuenow="' + Math.round(s.progress) + '"' +
               ' aria-valuemin="0"' +
               ' aria-valuemax="100"' +
               ' aria-label="Postęp do darmowej dostawy">' +
            '<div class="playio-progress__fill" style="width: ' + s.progress + '%"></div>' +
            (s.tier2Active && s.tier1MarkerPct > 0
              ? '<div class="playio-progress__tier playio-progress__tier--1"' +
                     ' style="left: ' + s.tier1MarkerPct + '%"' +
                     ' title="Darmowa dostawa"></div>'
              : '') +
          '</div>' +
        '</div>';
    }

    escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = String(str || '');
      return div.innerHTML;
    }
  }

  // --- Watcher ---

  class PlayioCartWatcher {
    constructor() {
      this.instances = [];
      this.giftManager = new PlayioGiftManager(window.PlayioConfig);
      this.previousCart = null;
      this.syncTimer = null;
      this.isSyncing = false;
      this.needsResync = false;
      this.ownDispatch = false;
      this.patchFetch();
      this.listenToEvents();
      this.watchDrawer();
    }

    register(instance) {
      if (instance) this.instances.push(instance);
    }

    scheduleSyncAndRefresh(delay) {
      if (this.syncTimer) clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(function () {
        this.syncTimer = null;
        this.syncAndRefresh();
      }.bind(this), delay || 300);
    }

    /**
     * [ROLLBACK 2026-05-28] DISABLED: nasz custom dispatch nadpisywał natywny Horizon
     * cart:update i powodował PUSTY DRAWER przy pierwszym add (blocker checkoutu).
     *
     * Historia:
     * - v1: dispatch z `data: {}` → cart-items-component fall-back na async fetch → drawer pusty 1-3s
     * - v2: dispatch z pre-fetched sections HTML → wciąż nie pomogło (timing/race z natywnym morph)
     * - v3 (TERAZ): NO-OP — natywny Horizon sam obsługuje drawer/header section morph po add.
     *   Ship-bar refresh: nasłuch natywnego cart:update (już w listenToEvents L502+) →
     *   scheduleSyncAndRefresh → render progress bars (bez dispatch'owania własnego eventu).
     *
     * TRADE-OFF: po addGift (origFetch /cart/add.js) drawer NIE WIE o nowym gift dopóki nie
     * przyjdzie kolejny natywny event (następny add z product page). Akceptowalny — gift dla
     * user'a widoczny w drawerze przy następnej interakcji. Drawer dziala = priorytet,
     * ship-bar/gift refresh = drobiazg.
     */
    async notifyThemeCartUpdate(cart) {
      // [2026-05-30] Re-enabled jako cienki most do drawera. addGift idzie przez origFetch
      // (bypass patchFetch) → brak natywnego cart:update z sekcjami. Drawer-side helper
      // (playio-drawer-morph.js) dociąga /?sections i robi setHTMLUnsafe → gift widoczny od razu.
      // Anti-pętla: helper dispatchuje cart:update z sekcjami → ten listener znów odpali syncGift,
      // ale findGiftInCart() widzi gift w koszyku → brak ponownego add. Bezpieczne.
      try {
        if (typeof window.__playioForceRerender === 'function') {
          await window.__playioForceRerender();
        }
      } catch (e) {
        console.warn('[Playio][Gift] notifyThemeCartUpdate error:', e);
      }
    }

    async syncAndRefresh() {
      if (this.isSyncing) {
        this.needsResync = true;
        return;
      }
      this.isSyncing = true;
      this.needsResync = false;

      var currentCart = null;
      try {
        currentCart = await origFetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); });
      } catch (err) {
        console.error('[Playio] cart fetch error:', err);
        this.isSyncing = false;
        if (this.needsResync) {
          this.needsResync = false;
          this.scheduleSyncAndRefresh(200);
        }
        return;
      }

      // Try gift sync — failure must NOT block rendering
      var changed = false;
      try {
        changed = await this.giftManager.syncGift(currentCart, this.previousCart);
        if (changed) {
          // Re-fetch fresh cart AFTER gift change, BEFORE dispatching event with sections
          try {
            currentCart = await origFetch('/cart.js', { credentials: 'same-origin' })
              .then(function (r) { return r.json(); });
          } catch (e) {}
          // Tell Horizon theme to re-render the cart section from server (now with pre-rendered sections HTML)
          await this.notifyThemeCartUpdate(currentCart);
        }
      } catch (giftErr) {
        console.error('[Playio] gift sync error:', giftErr);
      }

      this.previousCart = currentCart;

      // ALWAYS render all progress bars
      for (var i = 0; i < this.instances.length; i++) {
        try { this.instances[i].render(currentCart); } catch (e) {}
      }

      this.isSyncing = false;

      if (this.needsResync) {
        this.needsResync = false;
        this.scheduleSyncAndRefresh(100);
      }
    }

    watchDrawer() {
      var self = this;
      // Re-sync when cart drawer dialog opens (attribute 'open' added)
      function observeDialog() {
        var dialog = document.querySelector('cart-drawer-component dialog');
        if (!dialog) return;
        new MutationObserver(function () {
          if (dialog.hasAttribute('open') || dialog.open) {
            self.scheduleSyncAndRefresh(50);
          }
        }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
      }
      // Try immediately, or wait for DOM
      if (document.querySelector('cart-drawer-component dialog')) {
        observeDialog();
      } else {
        document.addEventListener('DOMContentLoaded', observeDialog);
      }
      // Re-sync on pageshow (handles bfcache and fresh navigations)
      window.addEventListener('pageshow', function () {
        self.scheduleSyncAndRefresh(100);
      });
    }

    patchFetch() {
      var self = this;
      window.fetch = async function () {
        var args = arguments;
        var response = await origFetch.apply(this, args);
        try {
          var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
          if (/\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
            self.scheduleSyncAndRefresh(300);
          }
        } catch (e) {}
        return response;
      };
    }

    listenToEvents() {
      var self = this;
      // Listen to Horizon's 'cart:update' + common cart events
      ['cart:update', 'cart:updated', 'cart:refresh', 'cart:change', 'cart.requestComplete',
       'cart-drawer:opened', 'cart-items:updated'
      ].forEach(function (ev) {
        document.addEventListener(ev, function () {
          if (!self.ownDispatch) self.scheduleSyncAndRefresh(200);
        });
      });

      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (form && form.matches && form.matches('form[action*="/cart/add"]')) {
          self.scheduleSyncAndRefresh(500);
        }
      });
    }
  }

  var watcher = new PlayioCartWatcher();
  var initDone = false;

  function initProgressBars() {
    var selector =
      (window.PlayioConfig && window.PlayioConfig.selectors.progressContainer) ||
      '[data-playio-progress]';
    var newFound = false;
    document.querySelectorAll(selector).forEach(function (el) {
      if (el.__playioInit) return;
      el.__playioInit = true;
      newFound = true;
      var instance = new PlayioShippingProgress(el);
      watcher.register(instance);
    });
    if (!initDone) {
      initDone = true;
      watcher.syncAndRefresh();
    } else if (newFound) {
      watcher.scheduleSyncAndRefresh(100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProgressBars);
  } else {
    initProgressBars();
  }

  var mutationTimer = null;
  if (document.body) {
    new MutationObserver(function () {
      if (mutationTimer) clearTimeout(mutationTimer);
      mutationTimer = setTimeout(initProgressBars, 200);
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.PlayioUpsell = {
    refresh: function () { watcher.syncAndRefresh(); },
    version: '1.4.0'
  };
})();
